import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Icon from "../../../components/AppIcon";
import {
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  staggerContainer,
  staggerItem,
  useInView,
} from "../../../utils/animations";

const BenefitsSection = () => {
  const [visibleCards, setVisibleCards] = useState([]);

  const benefits = [
    {
      id: "mobile-first",
      icon: "Smartphone",
      title: "Mobile-First Design",
      description:
        "Intuitive interface designed specifically for mobile devices. Manage your business with just a few taps.",
      metrics: [
        "Touch-optimized UI",
        "Quick actions menu",
        "Gesture navigation",
      ],
      color: "primary",
      caseStudy: {
        business: "Café Connect Owner",
        result: "Processes sales 3x faster on mobile than desktop",
        quote:
          "I can ring up customers while walking around the store. The mobile interface is so much faster than our old system.",
      },
    },
    {
      id: "offline-sync",
      icon: "Wifi",
      title: "Works Offline",
      description:
        "Never miss a sale with offline capability. All data syncs automatically when connection is restored.",
      metrics: [
        "Full offline functionality",
        "Auto-sync when online",
        "Data integrity guaranteed",
      ],
      color: "success",
      caseStudy: {
        business: "Food Truck Owner",
        result: "Zero sales lost due to poor connectivity",
        quote:
          "Whether I'm at a festival with no signal or in a parking lot, the app keeps working. Sales sync perfectly when I get back online.",
      },
    },
    {
      id: "real-time",
      icon: "Zap",
      title: "Real-Time Updates",
      description:
        "Instant synchronization across all devices. See live inventory, sales, and reports wherever you are.",
      metrics: [
        "Live data sync",
        "Multi-device support",
        "Instant notifications",
      ],
      color: "warning",
      caseStudy: {
        business: "Retail Chain Manager",
        result: "Monitors 3 stores in real-time from mobile",
        quote:
          "I get instant alerts when inventory is low or sales spike. I can manage all my stores from my phone during my commute.",
      },
    },
    {
      id: "secure",
      icon: "Shield",
      title: "Bank-Level Security",
      description:
        "Your business data is protected with enterprise-grade encryption and secure cloud storage.",
      metrics: [
        "256-bit encryption",
        "Secure cloud backup",
        "Privacy controls",
      ],
      color: "secondary",
      caseStudy: {
        business: "Electronics Store Owner",
        result: "Complete peace of mind with data security",
        quote:
          "Knowing my sales data and customer information is secure lets me focus on growing my business instead of worrying about security.",
      },
    },
  ];

  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardId = entry.target.getAttribute("data-card-id");
            setVisibleCards((prev) => [...new Set([...prev, cardId])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    benefits?.forEach((benefit) => {
      const element = document.getElementById(`benefit-${benefit?.id}`);
      if (element) observer?.observe(element);
    });

    return () => observer?.disconnect();
  }, []);

  const getColorClasses = (color) => {
    const colorMap = {
      success: {
        bg: "bg-success/10",
        text: "text-success",
        border: "border-success",
        gradient: "from-success/5 to-success/10",
      },
      primary: {
        bg: "bg-primary/10",
        text: "text-primary",
        border: "border-primary",
        gradient: "from-primary/5 to-primary/10",
      },
      secondary: {
        bg: "bg-secondary/10",
        text: "text-secondary",
        border: "border-secondary",
        gradient: "from-secondary/5 to-secondary/10",
      },
      warning: {
        bg: "bg-warning/10",
        text: "text-warning",
        border: "border-warning",
        gradient: "from-warning/5 to-warning/10",
      },
    };
    return colorMap?.[color];
  };

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <motion.section
      ref={ref}
      id="benefits"
      className="py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-white"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={staggerContainer}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div className="text-center mb-16" variants={staggerItem}>
          <motion.div
            className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6"
            variants={fadeInUp}
          >
            <Icon name="Smartphone" size={16} className="mr-2" />
            Mobile App Features
          </motion.div>
          <motion.h2
            className="text-3xl lg:text-5xl font-bold text-text-primary mb-6"
            variants={fadeInUp}
          >
            Business Management Made Mobile
          </motion.h2>
          <motion.p
            className="text-lg text-text-secondary max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            Experience the power of professional business management tools
            designed specifically for mobile. Manage your business effectively
            from anywhere, anytime.
          </motion.p>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          className="grid md:grid-cols-2 gap-8 mb-16"
          variants={staggerItem}
        >
          {benefits?.map((benefit, index) => {
            const colors = getColorClasses(benefit?.color);
            const isVisible = visibleCards?.includes(benefit?.id);
            const isExpanded = expandedCard === benefit?.id;

            return (
              <motion.div
                key={benefit?.id}
                id={`benefit-${benefit?.id}`}
                data-card-id={benefit?.id}
                className={`bg-white rounded-2xl p-8 shadow-lg border-2 transition-all duration-500 cursor-pointer backdrop-blur-sm ${
                  isExpanded
                    ? `${colors?.border} scale-105`
                    : "border-transparent hover:border-gray-200"
                }`}
                onClick={() => setExpandedCard(isExpanded ? null : benefit?.id)}
                variants={index % 2 === 0 ? fadeInLeft : fadeInRight}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                }}
                whileTap={{ scale: 0.98 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  delay: index * 0.1,
                }}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center">
                    <div
                      className={`w-16 h-16 ${colors?.bg} rounded-xl flex items-center justify-center mr-4`}
                    >
                      <Icon
                        name={benefit?.icon}
                        size={28}
                        className={colors?.text}
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-text-primary mb-2">
                        {benefit?.title}
                      </h3>
                      <p className="text-text-secondary">
                        {benefit?.description}
                      </p>
                    </div>
                  </div>
                  <Icon
                    name={isExpanded ? "ChevronUp" : "ChevronDown"}
                    size={20}
                    className={`transition-transform ${colors?.text}`}
                  />
                </div>
                {/* Metrics */}
                <div className="space-y-3 mb-6">
                  {benefit?.metrics?.map((metric, metricIndex) => (
                    <div key={metricIndex} className="flex items-center">
                      <Icon
                        name="Check"
                        size={16}
                        className={`${colors?.text} mr-3`}
                      />
                      <span className="text-text-secondary">{metric}</span>
                    </div>
                  ))}
                </div>
                {/* Expandable Case Study */}
                {isExpanded && (
                  <div
                    className={`bg-gradient-to-r ${colors?.gradient} rounded-lg p-6 border-l-4 ${colors?.border}`}
                  >
                    <div className="flex items-center mb-4">
                      <Icon
                        name="Building"
                        size={20}
                        className={colors?.text}
                      />
                      <h4 className="font-semibold text-text-primary ml-2">
                        Success Story
                      </h4>
                    </div>
                    <div className="mb-4">
                      <div className="font-medium text-text-primary">
                        {benefit?.caseStudy?.business}
                      </div>
                      <div className={`font-bold ${colors?.text} text-lg`}>
                        {benefit?.caseStudy?.result}
                      </div>
                    </div>
                    <blockquote className="text-text-secondary italic border-l-2 border-gray-300 pl-4">
                      "{benefit?.caseStudy?.quote}"
                    </blockquote>
                  </div>
                )}
                {/* Expand Indicator */}
                <div className="text-center mt-4">
                  <span className={`text-sm ${colors?.text} font-medium`}>
                    {isExpanded
                      ? "Click to collapse"
                      : "Click to see success story"}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Summary Stats */}
        <motion.div
          className="bg-white rounded-2xl p-8 shadow-lg backdrop-blur-sm border border-gray-100"
          variants={staggerItem}
          whileHover={{
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          }}
        >
          <motion.div className="text-center mb-8" variants={fadeInUp}>
            <motion.h3
              className="text-2xl font-bold text-text-primary mb-4"
              variants={fadeInUp}
            >
              Average Results Across All Users
            </motion.h3>
            <motion.p className="text-text-secondary" variants={fadeInUp}>
              Based on data from 5,000+ businesses using TAK Business Manager
            </motion.p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-4 gap-8"
            variants={staggerItem}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="TrendingUp" size={24} className="text-success" />
              </div>
              <div className="text-3xl font-bold text-success mb-2">25%</div>
              <div className="text-text-secondary">Average Profit Increase</div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Clock" size={24} className="text-primary" />
              </div>
              <div className="text-3xl font-bold text-primary mb-2">12</div>
              <div className="text-text-secondary">Hours Saved Per Week</div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Target" size={24} className="text-secondary" />
              </div>
              <div className="text-3xl font-bold text-secondary mb-2">80%</div>
              <div className="text-text-secondary">Reduction in Stockouts</div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={24} className="text-warning" />
              </div>
              <div className="text-3xl font-bold text-warning mb-2">98%</div>
              <div className="text-text-secondary">Customer Satisfaction</div>
            </div>
          </motion.div>
        </motion.div>

        {/* CTA */}
        <motion.div className="text-center mt-12" variants={staggerItem}>
          <motion.p
            className="text-lg text-text-secondary mb-6"
            variants={fadeInUp}
          >
            Ready to achieve these results for your business?
          </motion.p>
          <motion.button
            onClick={() =>
              document
                .getElementById("trial-signup")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center px-8 py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-cta backdrop-blur-sm"
            variants={fadeInUp}
            whileHover={{
              scale: 1.05,
              boxShadow: "0 20px 40px -12px rgba(59, 130, 246, 0.5)",
            }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Transformation
            <Icon name="ArrowRight" size={20} className="ml-2" />
          </motion.button>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default BenefitsSection;
